import { SEO, breadcrumbJsonLd } from "@/components/SEO";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Calendar, MapPin, Users, ArrowRight } from "lucide-react";
import { AREAS } from "@/data/areas";

export default function Experiencias() {
  const [items, setItems] = useState([]);
  const [area, setArea] = useState("todas");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (area !== "todas") params.area = area;
    api.get("/experiencias", { params }).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [area]);

  const areaDe = (e) => AREAS.find((a) => a.nombre === e.area);

  return (
    <div className="max-w-6xl mx-auto px-6 py-20 lg:py-16 md:py-24">
      <SEO title="Experiencias para adultos mayores en CDMX · Catálogo del mes Utopistas" description="Catálogo mensual de experiencias Utopistas para adultos mayores en Ciudad de México: tertulias, círculos de lectura, clases de idiomas, salidas al teatro, talleres de bienestar y charlas sobre herencia y finanzas." keywords={["catálogo experiencias adultos mayores", "talleres para adultos mayores CDMX"]} jsonLd={breadcrumbJsonLd([{ name: "Utopistas", path: "/marca" }, { name: "Experiencias", path: "/marca/experiencias" }])} />
      <p className="text-base tracking-[0.3em] text-cometa-marino/70 mb-3">CATÁLOGO DEL MES</p>
      <h1 className="text-4xl sm:text-5xl md:text-7xl leading-[0.95] mb-6">Seis áreas, muchas formas <em className="text-cometa-carmesi">de seguir descubriendo.</em></h1>
      <p className="text-xl text-cometa-tinta/80 max-w-3xl mb-10">Cada experiencia pertenece a un área y tiene un formato. Elige lo que te llame: puedes participar en una sola o en todas las que quieras.</p>

      <div className="flex flex-wrap gap-3 mb-12" role="group" aria-label="Filtrar por área">
        {[{ slug: "todas", nombre: "Todas" }, ...AREAS].map((a) => (
          <motion.button
            key={a.slug}
            onClick={() => setArea(a.slug === "todas" ? "todas" : a.nombre)}
            whileTap={{ scale: 0.95 }}
            data-testid={`filter-${a.slug}`}
            className={`btn px-6 py-3 rounded-full text-lg font-medium transition border-2 ${(area === "todas" ? "todas" : area) === (a.slug === "todas" ? "todas" : a.nombre) ? "bg-cometa-marino text-cometa-crema border-cometa-marino" : "bg-white border-cometa-marino/20 text-cometa-marino hover:border-cometa-marino"}`}
          >
            {a.nombre}
          </motion.button>
        ))}
      </div>

      {loading ? (
        <p className="text-center py-20 text-cometa-tinta/50">Cargando experiencias...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-cometa-marino/20 rounded-3xl">
          <p className="font-script text-4xl text-cometa-marino mb-3">Muy pronto.</p>
          <p className="text-xl text-cometa-tinta/60 max-w-lg mx-auto">Estamos preparando el catálogo de este mes. Suscríbete a la Carta desde la Cometa para enterarte apenas se abra.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
          {items.map((e, i) => {
            const a = areaDe(e);
            return (
            <motion.div key={e.slug} layout initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ delay: i * 0.05 }}>
            <Link to={`/marca/experiencias/${e.slug}`} data-testid={`exp-card-${e.slug}`} className="group block bg-white border border-cometa-marino/10 rounded-3xl overflow-hidden hover:shadow-xl transition hover:-translate-y-1">
              <div className="aspect-[16/9] bg-cometa-marino/10 relative" style={e.imagen ? { backgroundImage: `url(${e.imagen})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span className={`text-base font-semibold px-4 py-1.5 rounded-full shadow ${a ? a.c : "bg-cometa-dorado text-cometa-tinta"}`} data-testid={`exp-area-${e.slug}`}>{e.area || e.tipo}</span>
                  {e.formato && <span className="bg-white/90 text-cometa-tinta text-base font-semibold px-4 py-1.5 rounded-full shadow" data-testid={`exp-formato-${e.slug}`}>{e.formato}</span>}
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl md:text-3xl mb-4">{e.nombre}</h3>
                <div className="space-y-2 text-cometa-tinta/80 mb-6">
                  {e.guia_nombre && <p className="text-base"><span className="text-cometa-marino font-semibold">Nos acompaña:</span> {e.guia_nombre}</p>}
                  {e.fecha && <p className="flex items-center gap-2 text-base"><Calendar size={16} /> {new Date(e.fecha).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })} {e.hora && `· ${e.hora}`}</p>}
                  <p className="flex items-center gap-2 text-base capitalize"><MapPin size={16} /> {e.modalidad}</p>
                  <p className="flex items-center gap-2 text-base"><Users size={16} /> {e.inscritos}/{e.cupo_maximo}</p>
                </div>
                <p className="text-lg text-cometa-tinta/80 mb-6 line-clamp-2">{e.descripcion}</p>
                <div className="flex items-center justify-between pt-4 border-t border-cometa-tinta/10">
                  <div>
                    {e.precio ? <p className="text-2xl font-display text-cometa-marino">${e.precio} MXN</p> : <p className="text-base text-cometa-tinta/50 italic">Precio próximamente</p>}
                  </div>
                  <span className="inline-flex items-center gap-2 text-cometa-marino font-semibold text-lg group-hover:gap-3 transition">Quiero participar <ArrowRight size={18} /></span>
                </div>
              </div>
            </Link>
            </motion.div>
          ); })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
